"use client";

import { useState } from "react";
import {
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  CARD,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  fieldText,
  optionLabel,
  SiftQuestion,
} from "@/components/tools/research-discovery/sift-fields";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  allFields,
  answeredCount,
  fieldCount,
  moveProgress,
  SIFT_CONCLUSION,
  SIFT_HEADER,
  SIFT_MOVES,
  type SiftState,
} from "@/lib/tool-kit/research-discovery/sift";

/** English strings. A translated content file overrides these through `ui`. */
export const SIFT_METHOD_WORKSHEET_STRINGS = {
  worksheetTitle: "SIFT Method Worksheet",
  intro:
    "SIFT is Mike Caulfield's four-move habit for checking information online: Stop, Investigate the source, Find better coverage, and Trace claims, quotes and media to the original context. The questions below are this worksheet's prompts for each move.",
  dateLabel: "Date",
  fields: {
    topic: {
      label: "What you are checking — the claim, post or page",
      placeholder: "e.g. A viral post saying a vitamin supplement cures colds",
    },
    where: {
      label: "Where you found it",
      placeholder: "Link, app or account name",
    },
    stopKnow: {
      label:
        "Do I already know this source — the author and the site — and have I a reason to trust it?",
    },
    stopPurpose: {
      label: "What am I trying to do with this?",
      hint: "Reading for fun needs a lighter check than citing it in an essay or sharing it with others.",
      placeholder: "e.g. Decide whether to cite it in my essay",
    },
    stopFeeling: {
      label: "How does the claim make me feel?",
      hint: "A strong reaction — anger, excitement, feeling proved right — is a cue to check before you share.",
    },
    invWho: {
      label: "Who is behind this source?",
      placeholder: "Author, organisation or website",
    },
    invExpertise: {
      label: "What expertise or track record do they have on this topic?",
    },
    invAgenda: {
      label:
        "What might they want from me — an agenda, a product, a funder, a position?",
    },
    invChecked: {
      label: "Which independent pages did I check about them?",
      hint: "Open new tabs and see what others say about the source — a Wikipedia article, a news search, a fact-checker — instead of reading its own “About” page alone.",
    },
    invVerdict: { label: "Is this source suited to my purpose?" },
    claim: { label: "The claim, in one sentence" },
    betterSources: {
      label: "Better sources I found on the claim",
      hint: "Title, publisher and link — a reference work, a fact-check, trusted reporting, or the research itself.",
    },
    consensus: { label: "What does the better coverage say about the claim?" },
    original: {
      label: "Where did the claim, quote, image or video originally come from?",
      placeholder: "Link or description of the original",
    },
    foundOriginal: { label: "Did I find the original?" },
    context: { label: "Does the original match how it was presented?" },
    traceNotes: {
      label: "What changed between the original and the version I saw?",
      hint: "Cropped, clipped, out of date, quoted without its next sentence, a study that does not support the claim.",
    },
    verdict: { label: "My conclusion about the claim" },
    nextStep: { label: "What I will do" },
    finalNotes: { label: "Notes" },
  },
  options: {
    stopKnow: {
      yes: "Yes, I know it and have a reason to trust it",
      unsure: "I have heard of it, but I am not sure",
      no: "No, I don't know it",
    },
    stopFeeling: {
      calm: "Calm or neutral",
      strong: "Strongly — I want to share it or argue with it",
    },
    invVerdict: {
      yes: "Yes",
      caution: "With caution",
      no: "No",
      unclear: "Can't tell",
    },
    consensus: {
      agrees: "It agrees",
      disagrees: "It disagrees",
      divided: "Experts are divided",
      none: "I couldn't find any",
    },
    foundOriginal: { yes: "Yes", no: "No" },
    context: {
      accurate: "Yes, accurately",
      partly: "Partly — something was left out or changed",
      no: "No",
      unclear: "Couldn't tell",
    },
    verdict: {
      supported: "Supported",
      partly: "Partly supported, or missing context",
      unsupported: "Not supported",
      unverified: "I could not verify it",
    },
    nextStep: {
      use: "Use it",
      caveats: "Use it, with the caveats noted",
      better: "Look for a better source",
      drop: "Don't use or share it",
    },
  },
  moves: {
    stop: {
      name: "S — Stop",
      summary:
        "Before you read or share, ask whether you know the source and its reputation. And if fact-checking pulls you off track, stop and recall your purpose.",
    },
    investigate: {
      name: "I — Investigate the source",
      summary:
        "Know what you are reading before you read it. A minute on who is behind it and what they know is usually enough.",
    },
    find: {
      name: "F — Find better coverage",
      summary:
        "Set the article aside and look for trusted coverage of the claim itself, and for whether experts agree.",
    },
    trace: {
      name: "T — Trace claims, quotes and media to the original context",
      summary:
        "Find where the quote, image, video or study came from, and check that what you saw is what the original said.",
    },
  },
  conclusionHeading: "Conclusion",
  progressHeading: "Progress",
  statAnswered: "Questions answered",
  statTotal: "Questions on the worksheet",
  moveProgress: "{answered} of {total} answered",
  empty: "Start with the first move: Stop, and note what you are checking.",
  credit:
    "SIFT is by Mike Caulfield: “SIFT (The Four Moves)”, hapgood.us, 19 June 2019, licensed CC BY 4.0. His related course material is “Web Literacy for Student Fact-Checkers”. The worksheet questions are CitationEasy's own.",
  print: "Print worksheet",
  copySummary: "Copy summary",
  copied: "Copied!",
  reset: "Start again",
  summaryTitle: "SIFT worksheet — {date}",
  summaryUndated: "SIFT worksheet",
} as const;

export type SiftWorksheetStrings = typeof SIFT_METHOD_WORKSHEET_STRINGS;

function summaryLines(
  strings: SiftWorksheetStrings,
  state: SiftState,
  date: string
): string {
  const heading =
    date === "" ? strings.summaryUndated : t(strings.summaryTitle, { date });
  const lines = [heading];
  for (const field of allFields()) {
    const value = (state[field.key] ?? "").trim();
    if (value !== "") {
      const shown =
        field.kind === "choice"
          ? optionLabel(strings, field.key, value)
          : value;
      lines.push(`${fieldText(strings, field.key).label} ${shown}`);
    }
  }
  return lines.join("\n");
}

export default function SiftMethodWorksheetWidget() {
  const strings = useToolUi(SIFT_METHOD_WORKSHEET_STRINGS);
  const fmt = useToolNumber();
  const [state, setState] = useState<SiftState>({});
  const [date, setDate] = useState("");

  function change(key: string) {
    return (value: string) =>
      setState((current) => ({ ...current, [key]: value }));
  }

  const answered = answeredCount(state);
  return (
    <div className="space-y-6">
      <h2 className="hidden font-semibold text-2xl text-foreground print:block">
        {strings.worksheetTitle}
      </h2>
      <div className="print:hidden">
        <Notice>{strings.intro}</Notice>
      </div>
      <section className={`${CARD} grid gap-4 sm:grid-cols-3`}>
        <div className="sm:col-span-2">
          {SIFT_HEADER.map((field) => (
            <div className="mb-4 last:mb-0" key={field.key}>
              <SiftQuestion
                field={field}
                onChange={change(field.key)}
                strings={strings}
                value={state[field.key] ?? ""}
              />
            </div>
          ))}
        </div>
        <TextField
          id="sift-date"
          label={strings.dateLabel}
          onChange={setDate}
          type="date"
          value={date}
        />
      </section>

      {SIFT_MOVES.map((move) => {
        const progress = moveProgress(state, move);
        const info = strings.moves[move.key];
        return (
          <section
            className={`${CARD} break-inside-avoid space-y-4`}
            key={move.key}
          >
            <div>
              <h3 className={WIDGET_H3}>{info.name}</h3>
              <p className="text-muted-foreground text-sm">{info.summary}</p>
              <p className={`${HINT} print:hidden`}>
                {t(strings.moveProgress, {
                  answered: fmt(progress.answered),
                  total: fmt(progress.total),
                })}
              </p>
            </div>
            {move.fields.map((field) => (
              <SiftQuestion
                field={field}
                key={field.key}
                onChange={change(field.key)}
                strings={strings}
                value={state[field.key] ?? ""}
              />
            ))}
          </section>
        );
      })}

      <section className={`${CARD} break-inside-avoid space-y-4`}>
        <h3 className={WIDGET_H3}>{strings.conclusionHeading}</h3>
        {SIFT_CONCLUSION.map((field) => (
          <SiftQuestion
            field={field}
            key={field.key}
            onChange={change(field.key)}
            strings={strings}
            value={state[field.key] ?? ""}
          />
        ))}
      </section>

      <p className={HINT}>{strings.credit}</p>

      <section
        aria-live="polite"
        className={`${RESULT_CARD} space-y-4 print:hidden`}
      >
        <h2 className={WIDGET_H2}>{strings.progressHeading}</h2>
        <StatGrid>
          <StatCard
            emphasis
            label={strings.statAnswered}
            value={fmt(answered)}
          />
          <StatCard label={strings.statTotal} value={fmt(fieldCount())} />
        </StatGrid>
        {answered === 0 ? <Notice>{strings.empty}</Notice> : null}
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_PRIMARY}
            onClick={() => window.print()}
            type="button"
          >
            {strings.print}
          </button>
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copySummary}
            text={summaryLines(strings, state, date)}
          />
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              setState({});
              setDate("");
            }}
            type="button"
          >
            {strings.reset}
          </button>
        </div>
      </section>
    </div>
  );
}
